<template>
	<f7-list class="input-amount list-strong list-outline">
		<f7-block-header
			v-if="headerTitle?.length || $slots['header-end']"
			class="input-amount__header"
		>
			<span v-if="headerTitle?.length" class="input-amount__header-title">{{ headerTitle }}</span>
			<div v-if="$slots['header-end']" class="input-amount__header-end">
				<slot name="header-end" />
			</div>
		</f7-block-header>
		<f7-list-input
			ref="inputAmount"
			type="text"
			inputmode="decimal"
			class="input-amount__input"
			v-model:value="localStringAmount"
			@beforeinput="beforeInputHandler"
			@focus="focusInputHandler"
			@blur="blurInputHandler"
      :disabled="disabled"
		>
			<template v-if="$slots['content']" #content>
				<div class="input-amount__input-content">
					<slot name="content" />
				</div>
			</template>
		</f7-list-input>
		<div v-if="$slots['hint']" class="input-amount__hint">
			<slot name="hint" />
		</div>
	</f7-list>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import UtilsService from "@/services/others/UtilsService";

interface IProps {
	headerTitle?: string;
	focused?: boolean;
	currency?: "USD" | "USDT" | "RUB" | "KZT";
	modelValue?: number;
  disabled?: boolean;
}

interface Emits {
	(e: "update:modelValue", value: number): void;
	(e: "blur"): void;
	(e: "focus"): void;
	(e: "update:focused", value: boolean): void;
}

const props = withDefaults(defineProps<IProps>(), {
	currency: "RUB",
	modelValue: 0,
	stringAmount: "0",
  disabled: false,
});
const emits = defineEmits<Emits>();

const inputAmount = ref<HTMLInputElement | null>(null);
const localFocused = ref<boolean>(props.focused ?? false);
const savedAmountBeforeFocus = ref<string>(props.modelValue.toString());

const savedStringAmount = ref<string>(props.modelValue.toString());

const utilsService = UtilsService.of();

const currencyFormatter = computed(() => utilsService.currencyFormatter(props.currency));

const localStringAmount = computed({
	get: () => {
		if (localFocused.value) {
			return savedStringAmount.value.replace(/[^\d.]/g, '');
		} else {
			savedStringAmount.value = currencyFormatter.value.format(props.modelValue);
			return currencyFormatter.value.format(props.modelValue);
		}
	},
	set: (newValue: string) => {
		savedStringAmount.value = newValue;
		const parsed = parseFormattedCurrency(newValue);
		emits("update:modelValue", parsed);
	}
});

watch(localStringAmount, (newValue) => {
	emits("update:modelValue", parseFormattedCurrency(newValue));
});

watch(() => props.focused, (val) => {
	localFocused.value = val;
	if (!val && inputAmount.value) {
		inputAmount.value!.blur();
	}
});

onMounted(() => {
	initInputAmountElement();
});

const initInputAmountElement = () => {
	if (inputAmount.value) {
		// @ts-ignore
		inputAmount.value = inputAmount.value.$el?.querySelector("input") || inputAmount.value;
	}
};

const blurInputHandler = () => {
	if (!savedStringAmount.value.trim()) {
		savedStringAmount.value = savedAmountBeforeFocus.value;
	}

	savedStringAmount.value = currencyFormatter.value.format(props.modelValue);
	localFocused.value = false;
	emits("update:focused", false);
	emits("blur");
};

const focusInputHandler = () => {
	savedAmountBeforeFocus.value = savedStringAmount.value;
	localFocused.value = true;
	emits("update:focused", true);
	emits("focus");
};

const beforeInputHandler = (e: InputEvent) => {
	const input = e.target as HTMLInputElement;
	const newChar = e.data;
	const currentValue = input.value;
	const selectionStart = input.selectionStart ?? currentValue.length;
	const selectionEnd = input.selectionEnd ?? currentValue.length;

	if (e.inputType.startsWith("delete") || !newChar) return;

	const isDigit = /\d/.test(newChar);
	const isComma = newChar === ".";

	if (!isDigit && !isComma) return e.preventDefault();
	if (isComma && (currentValue.includes(".") || currentValue === "")) return e.preventDefault();

	const newValue = currentValue.slice(0, selectionStart) + newChar + currentValue.slice(selectionEnd);
	const [integerPart, fractionalPart] = newValue.split(".");

	const insertingLeadingZero =
		isDigit &&
		currentValue.startsWith("0") &&
		!currentValue.includes(".") &&
		selectionStart === 1 &&
		currentValue.length === 1 &&
		newChar !== ".";

	const isInvalidLeadingZero = isDigit && /^0\d/.test(integerPart);

	const isReplacingWhole = selectionStart === 0 && selectionEnd === currentValue.length;
	if ((insertingLeadingZero || isInvalidLeadingZero) && !isReplacingWhole) {
		return e.preventDefault();
	}

	if (fractionalPart?.length > 2) return e.preventDefault();
};


const parseFormattedCurrency = (input: string): number => {
	return Number(
		input
			.replace(/[^\d,.]/g, '')
			.replace(',', '.')
	)
}
</script>

<style scoped lang="scss">
.input-amount {
	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		font-style: normal;
		font-weight: 400;
		font-size: 12px;
		line-height: 120%;
		color: #818E95;
	}

	&__header-title {}

	&__header-end {
		margin-left: auto;
	}

	&__input {
		&:deep(input) {
			height: 62px;
			font-weight: 400;
			font-size: 26px;
			line-height: 120%;
			letter-spacing: -0.41px;
		}
	}

	&__input-content {
		height: 62px;
		display: flex;
		align-items: center;
		justify-content: center;
		padding-right: 1rem;
		gap: 1rem;
	}

	&__hint {
		@include text(12px, 400, var(--grey2));

		margin-top: 8px;
		padding-left: calc(var(--f7-list-item-padding-horizontal) + var(--f7-safe-area-left));
		padding-right: calc(var(--f7-list-item-padding-horizontal) + var(--f7-safe-area-right));
	}
}
</style>
